import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Check, X, DownloadSimple, Cpu, LockSimple, WarningCircle } from '@phosphor-icons/react';
import { LOCAL_MODELS, probeWebGPU, getCachedModels, downloadModel, recommendTier, tierFits } from '@/lib/localEngine';

export default function ModelManager({ open, onOpenChange }) {
  const [gpu, setGpu] = useState(null);
  const [cached, setCached] = useState({});
  const [downloading, setDownloading] = useState(null); // tier
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    // Guard: adapter probes can stall while the GPU is busy
    const probe = await Promise.race([
      probeWebGPU(true),
      new Promise(r => setTimeout(() => r(null), 8000)),
    ]);
    if (probe) setGpu(probe);
    else setGpu(g => g || { supported: false, reason: 'GPU probe timed out — try again in a moment.' });
    setCached(await getCachedModels());
  }, []);

  useEffect(() => { if (open) { setError(null); refresh(); } }, [open, refresh]);

  const startDownload = async (tier) => {
    setDownloading(tier);
    setError(null);
    setProgress({ progress: 0, text: 'Starting…' });
    try {
      await downloadModel(tier, (p) => setProgress(p));
      setCached(await getCachedModels()); // no GPU re-probe — it may be busy warming the model
    } catch (e) {
      setError(e?.message === 'webgpu-unavailable' ? (gpu?.reason || 'WebGPU unavailable') : (e?.message || 'Download failed'));
    } finally {
      setDownloading(null);
      setProgress(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]" data-testid="model-manager-modal">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold flex items-center gap-2" style={{ fontFamily: 'Clash Display, sans-serif' }}>
            <Cpu weight="duotone" className="w-5 h-5 text-lime-400" /> On-device models
          </DialogTitle>
          <DialogDescription className="text-sm text-white/50 pt-1">
            Download Lindey models to your device ahead of time — chats then run instantly, privately, with zero servers.
          </DialogDescription>
        </DialogHeader>

        {/* Device status */}
        <div className="rounded-xl bg-white/5 border border-white/10 p-3" data-testid="device-status">
          {gpu === null ? (
            <p className="text-xs text-white/40">Checking your device…</p>
          ) : gpu.supported ? (
            <div className="flex items-center gap-2 text-xs">
              <Check weight="bold" className="w-3.5 h-3.5 text-lime-400 shrink-0" />
              <span className="text-lime-300 font-medium">Device ready for on-device AI</span>
              <span className="text-white/35 font-mono ml-auto">{gpu.f16 ? 'f16' : 'f32'} · {gpu.maxBufferMB ? `${Math.round(gpu.maxBufferMB / 1024 * 10) / 10} GB buffer` : 'GPU'}</span>
            </div>
          ) : (
            <div className="flex items-start gap-2 text-xs" data-testid="device-status-unavailable">
              <WarningCircle weight="fill" className="w-3.5 h-3.5 text-orange-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-orange-300 font-medium">On-device AI unavailable on this browser</p>
                <p className="text-white/45 mt-0.5 leading-relaxed">{gpu.reason}</p>
                <p className="text-white/35 mt-1">Lindey will use stateless Private Cloud Compute instead — nothing is logged or retained.</p>
              </div>
            </div>
          )}
        </div>

        {/* Models */}
        <div className="space-y-2">
          {Object.entries(LOCAL_MODELS).map(([tier, m]) => (
            <div key={tier} className="flex items-center gap-3 rounded-xl bg-white/5 border border-white/10 px-3 py-2.5" data-testid={`model-row-${tier}`}>
              <div className="w-8 h-8 rounded-lg bg-lime-400/10 border border-lime-400/20 flex items-center justify-center shrink-0">
                <LockSimple weight="fill" className="w-3.5 h-3.5 text-lime-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-white/90">{m.label}</p>
                  <span className="text-[10px] font-mono text-white/35">{m.size}</span>
                  {gpu?.supported && recommendTier(gpu) === tier && (
                    <span className="text-[9px] font-semibold uppercase tracking-wide text-lime-300 bg-lime-400/10 border border-lime-400/20 px-1.5 py-0.5 rounded" data-testid={`model-recommended-${tier}`}>Recommended</span>
                  )}
                </div>
                {downloading === tier && progress ? (
                  <div className="mt-1.5">
                    <div className="h-1 rounded-full bg-white/10 overflow-hidden">
                      <div className="h-full energy-bar rounded-full transition-all duration-300" style={{ width: `${Math.max(3, (progress.progress || 0) * 100)}%` }} />
                    </div>
                    <p className="text-[10px] text-white/35 mt-1 truncate">{Math.round((progress.progress || 0) * 100)}% — {progress.text}</p>
                  </div>
                ) : (
                  <p className="text-[11px] text-white/40">{m.desc}</p>
                )}
              </div>
              {cached[tier] ? (
                <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-lime-300 bg-lime-400/10 border border-lime-400/20 px-2 py-1 rounded-md shrink-0" data-testid={`model-downloaded-${tier}`}>
                  <Check weight="bold" className="w-3 h-3" /> Ready
                </span>
              ) : gpu?.supported && !tierFits(tier, gpu) ? (
                <span className="text-[10px] font-medium text-white/35 bg-white/5 border border-white/10 px-2 py-1 rounded-md shrink-0" data-testid={`model-too-big-${tier}`}>
                  Needs more GPU memory
                </span>
              ) : (
                <button
                  onClick={() => startDownload(tier)}
                  disabled={!!downloading || gpu === null || (gpu && !gpu.supported)}
                  data-testid={`model-download-${tier}`}
                  className="flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-lg bg-lime-500 hover:bg-lime-600 text-black transition-colors disabled:opacity-35 shrink-0"
                >
                  {downloading === tier ? 'Downloading…' : <><DownloadSimple weight="bold" className="w-3 h-3" /> Download</>}
                </button>
              )}
            </div>
          ))}
        </div>

        {error && (
          <p className="text-xs text-red-300 bg-red-400/10 border border-red-400/20 rounded-lg px-3 py-2" data-testid="model-manager-error">
            <X weight="bold" className="w-3 h-3 inline mr-1" />{error}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
